import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Spinner from '../components/Spinner';
import TripDayCard from '../components/trip/TripDayCard';
import TripItemPackingList from '../components/trip/TripItemPackingList';
import TripChecklistSection from '../components/trip/TripChecklistSection';
import TripPackingCheckSection from '../components/trip/TripPackingCheckSection';
import TripSummarySection from '../components/trip/TripSummarySection';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {
    addChecklistItem,
    changeTripStatus,
    deleteChecklistItem,
    deleteTrip,
    fetchOutfitPlan,
    fetchTrip,
    fetchTripAnalytics,
    fetchTripChecklist,
    fetchTripItemList,
    formatTripDates,
    generateOutfitPlan,
    TripAnalytics,
    TripChecklist,
    TripChecklistCategory,
    TripChecklistItem,
    TripDetail,
    TripItem,
    TripItemList,
    TripItemStatus,
    TripOutfitPlan,
    TripStatus,
    TRIP_STATUS_LABELS,
    unlockTripDay,
    updateChecklistItem,
    updateTripItemStatus,
} from '../service/TripApi';

const LOAD_ERROR = 'Не удалось загрузить поездку. Попробуйте ещё раз.';

/** Текст ошибки операции: описание backend, иначе общий fallback. */
export const tripActionErrorText = (err: unknown, fallback: string): string => {
    const data = (err as {response?: {data?: {error?: string; description?: string}}} | undefined)?.response?.data;
    return data?.description?.trim() || data?.error?.trim() || fallback;
};

const TripDetailPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();

    const [trip, setTrip] = useState<TripDetail | null>(null);
    const [plan, setPlan] = useState<TripOutfitPlan | null>(null);
    const [itemList, setItemList] = useState<TripItemList | null>(null);
    const [checklist, setChecklist] = useState<TripChecklist | null>(null);
    const [analytics, setAnalytics] = useState<TripAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);

    useDocumentTitle(trip?.name);

    const load = useCallback(async () => {
        if (!id) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [tripData, planData, items, check] = await Promise.all([
                fetchTrip(id), fetchOutfitPlan(id), fetchTripItemList(id), fetchTripChecklist(id),
            ]);
            setTrip(tripData);
            setPlan(planData);
            setItemList(items);
            setChecklist(check);
            // Итоги существуют только у завершённой поездки: для остальных запрос вернёт 422,
            // и дёргать его незачем.
            setAnalytics(tripData.status === 'COMPLETED' ? await fetchTripAnalytics(id) : null);
        } catch (e) {
            setError(LOAD_ERROR);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const generate = async () => {
        if (!id) {
            return;
        }
        setGenerating(true);
        setActionError(null);
        try {
            setPlan(await generateOutfitPlan(id));
            setItemList(await fetchTripItemList(id));
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось собрать план образов.'));
        } finally {
            setGenerating(false);
        }
    };

    const unlockDay = async (dayId: string) => {
        if (!id) {
            return;
        }
        setBusyId(dayId);
        try {
            setPlan(await unlockTripDay(id, dayId));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось снять фиксацию дня.'));
        } finally {
            setBusyId(null);
        }
    };

    const changeItemStatus = async (item: TripItem, status: TripItemStatus) => {
        if (!id) {
            return;
        }
        setBusyId(item.id);
        try {
            await updateTripItemStatus(id, item.id, status);
            setItemList(await fetchTripItemList(id));
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось изменить статус вещи.'));
        } finally {
            setBusyId(null);
        }
    };

    const toggleChecklistItem = async (item: TripChecklistItem, done: boolean) => {
        if (!id) {
            return;
        }
        setBusyId(item.id);
        try {
            await updateChecklistItem(id, item.id, {done});
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось обновить пункт чек-листа.'));
        } finally {
            setBusyId(null);
        }
    };

    const addItem = async (category: TripChecklistCategory, title: string) => {
        if (!id) {
            return;
        }
        try {
            await addChecklistItem(id, category, title);
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось добавить пункт.'));
        }
    };

    const removeChecklistItem = async (item: TripChecklistItem) => {
        if (!id) {
            return;
        }
        setBusyId(item.id);
        try {
            await deleteChecklistItem(id, item.id);
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось удалить пункт.'));
        } finally {
            setBusyId(null);
        }
    };

    /** Список вещей и чек-лист после применения проверки сборов: статусы упаковки изменились. */
    const reloadPacking = async () => {
        if (!id) {
            return;
        }
        try {
            setItemList(await fetchTripItemList(id));
            setChecklist(await fetchTripChecklist(id));
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось обновить список вещей.'));
        }
    };

    const transition = async (status: TripStatus) => {
        if (!id) {
            return;
        }
        try {
            const updated = await changeTripStatus(id, status);
            setTrip(updated);
            setAnalytics(updated.status === 'COMPLETED' ? await fetchTripAnalytics(id) : null);
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось изменить статус поездки.'));
        }
    };

    const removeTrip = async () => {
        if (!id || !window.confirm('Удалить поездку вместе с планом и чек-листом?')) {
            return;
        }
        try {
            await deleteTrip(id);
            navigate('/trips');
        } catch (e) {
            setActionError(tripActionErrorText(e, 'Не удалось удалить поездку.'));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12" data-testid="trip-detail-loading">
                <Spinner/>
            </div>
        );
    }

    if (error || !trip) {
        return (
            <div role="alert" data-testid="trip-detail-error"
                 className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300 flex items-center justify-between gap-4">
                <span>{error ?? LOAD_ERROR}</span>
                <button onClick={load} className="underline min-h-[44px]">Повторить</button>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <header className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{trip.name}</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {[trip.city, trip.country].filter(Boolean).join(', ') || 'Направление не указано'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {formatTripDates(trip.startDate, trip.endDate)} · {trip.dayCount} дн. ·{' '}
                            <span data-testid="trip-status">{TRIP_STATUS_LABELS[trip.status]}</span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => navigate(`/trips/${trip.id}/edit`)} data-testid="trip-edit"
                                className="min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                            Изменить
                        </button>
                        <button onClick={removeTrip} data-testid="trip-delete"
                                className="min-h-[44px] px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400">
                            Удалить
                        </button>
                    </div>
                </div>

                {trip.allowedTransitions.length > 0 && (
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Смена статуса">
                        {trip.allowedTransitions.map(status => (
                            <button key={status} onClick={() => transition(status)}
                                    data-testid={`trip-status-${status}`}
                                    className="min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200">
                                {TRIP_STATUS_LABELS[status]}
                            </button>
                        ))}
                    </div>
                )}

                {actionError && (
                    <div role="alert" data-testid="trip-action-error"
                         className="p-3 rounded-lg border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                        {actionError}
                    </div>
                )}
            </header>

            <section className="space-y-3" aria-labelledby="trip-plan-title">
                <div className="flex items-center justify-between gap-3">
                    <h2 id="trip-plan-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Образы по дням
                    </h2>
                    <button onClick={generate} disabled={generating} data-testid="trip-plan-generate"
                            className="btn-primary min-h-[44px]">
                        {generating ? 'Собираем…' : 'Собрать план'}
                    </button>
                </div>
                {generating && (
                    <div className="flex justify-center py-6" data-testid="trip-plan-loading"><Spinner/></div>
                )}
                {!generating && (!plan || plan.days.length === 0) ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-plan-empty">
                        План ещё не собран. Нажмите «Собрать план» — образы подберутся под погоду места
                        назначения и события поездки.
                    </p>
                ) : (
                    !generating && plan && (
                        <ul className="grid gap-3" data-testid="trip-plan-days">
                            {plan.days.map(day => (
                                <TripDayCard key={day.id} day={day} onUnlock={unlockDay} busy={busyId === day.id}/>
                            ))}
                        </ul>
                    )
                )}
            </section>

            <section className="space-y-3" aria-labelledby="trip-items-title">
                <h2 id="trip-items-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Вещи
                </h2>
                {!itemList || itemList.items.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-items-empty">
                        Список вещей появится после сборки плана образов.
                    </p>
                ) : (
                    <TripItemPackingList
                        items={itemList.items}
                        packedPercent={itemList.packedPercent}
                        packedCount={itemList.packedCount}
                        totalItems={itemList.totalItems}
                        onStatusChange={changeItemStatus}
                        busyId={busyId}
                    />
                )}
            </section>

            <section className="space-y-3" aria-labelledby="trip-check-title">
                <h2 id="trip-check-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Проверка сборов
                </h2>
                <TripPackingCheckSection
                    tripId={trip.id}
                    hasItems={Boolean(itemList && itemList.items.length > 0)}
                    onApplied={reloadPacking}
                    onError={setActionError}
                />
            </section>

            {trip.status === 'COMPLETED' && analytics && (
                <section className="space-y-3" aria-labelledby="trip-summary-title">
                    <h2 id="trip-summary-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Итоги поездки
                    </h2>
                    <TripSummarySection
                        tripId={trip.id}
                        analytics={analytics}
                        onReported={setAnalytics}
                        onError={setActionError}
                    />
                </section>
            )}

            <section className="space-y-3" aria-labelledby="trip-checklist-title">
                <h2 id="trip-checklist-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Чек-лист
                </h2>
                {checklist && (
                    <TripChecklistSection
                        checklist={checklist}
                        onToggle={toggleChecklistItem}
                        onAdd={addItem}
                        onDelete={removeChecklistItem}
                        busyId={busyId}
                    />
                )}
            </section>
        </div>
    );
};

export default TripDetailPage;
